// Axios client for PJ1 OS. NEVER hardcode the backend URL — use env var.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // Fail fast in dev rather than ship a misconfigured build.
   
  console.error("[pj1os] REACT_APP_BACKEND_URL is not set");
}
export const API = `${BACKEND_URL}/api`;

const tokenKey = "pj1os.token";

export function getToken() {
  return localStorage.getItem(tokenKey);
}
export function setToken(t) {
  if (t) localStorage.setItem(tokenKey, t);
  else localStorage.removeItem(tokenKey);
}

export const api = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setToken(null);
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  },
);

// Convenience helpers
export const auth = {
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  register: (body) => api.post("/auth/register", body).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const ops = {
  kpis: () => api.get("/ops/analytics/kpis").then((r) => r.data),
  funnel: (days = 30) => api.get(`/ops/analytics/funnel?days=${days}`).then((r) => r.data),
  pipeline: (days = 30) => api.get(`/ops/analytics/pipeline?days=${days}`).then((r) => r.data),
  adaptersHealth: () => api.get("/ops/adapters/health").then((r) => r.data),
  adaptersReport: () => api.get("/ops/adapters/report").then((r) => r.data),
  // Public observability probes (no auth) — handy for an ops status widget.
  healthAlerts: () => api.get("/health/alerts").then((r) => r.data),
  metrics: () => api.get("/health/metrics").then((r) => r.data),
  hermesStatus: () => api.get("/ops/hermes/status").then((r) => r.data),
  agentTasks: (limit = 100) => api.get(`/ops/agent-tasks?limit=${limit}`).then((r) => r.data),
  audit: (params = {}) =>
    api
      .get("/ops/audit", { params })
      .then((r) => r.data),
  complianceQueue: () => api.get("/ops/compliance/queue").then((r) => r.data),
  complianceDecision: (id, decision, note) =>
    api.post(`/ops/compliance/${id}/decision`, { decision, note }).then((r) => r.data),
  emptyLegs: () => api.get("/ops/empty-legs").then((r) => r.data),
  llmCalls: (limit = 100) => api.get(`/ops/llm/calls?limit=${limit}`).then((r) => r.data),
  llmSummary: () => api.get("/ops/llm/summary").then((r) => r.data),
  // Ops command center — booking board / work queue / booking drawer actions.
  bookingsBoard: () => api.get("/ops/bookings/board").then((r) => r.data),
  queue: () => api.get("/ops/queue").then((r) => r.data),
  bookingPayments: (id) => api.get(`/ops/bookings/${id}/payments`).then((r) => r.data),
  bookingDocuments: (id) => api.get(`/ops/bookings/${id}/documents`).then((r) => r.data),
  recordPayment: (id, body) =>
    api.post(`/ops/bookings/${id}/record-payment`, body).then((r) => r.data),
  refundPayment: (paymentId, amount_usd = null) =>
    api.post(`/ops/payments/${paymentId}/refund`, { amount_usd }).then((r) => r.data),
  agentTasksByState: (state, limit = 100) =>
    api.get("/ops/agent-tasks", { params: { state, limit } }).then((r) => r.data),
  resolveAgentTask: (id, outcome = "succeeded", note = null) =>
    api.post(`/ops/agent-tasks/${id}/resolve`, { outcome, note }).then((r) => r.data),
  holds: (params = {}) => api.get("/ops/holds", { params }).then((r) => r.data),
};

// Sales Navigator prospect import — ingests a list the OWNER manually exported
// (provenance salesnav_export, SANCTIONED). The import NEVER sends and NEVER
// scrapes; it just creates compliant leads. LinkedIn-handle prospects then flow
// into the /ops/social approval queue as draft connect-messages automatically.
export const prospects = {
  // POST /api/ops/prospects/import-salesnav { csv_text } -> import summary
  // { imported, duplicates, skipped, skipped_reasons?[] }.
  import: (csvText) =>
    api.post("/ops/prospects/import-salesnav", { csv_text: csvText }).then((r) => r.data),
  // GET /api/ops/prospects -> recently imported prospects (array or { prospects:[] }).
  list: (limit = 100) =>
    api.get("/ops/prospects", { params: { limit } }).then((r) => r.data),
};

// STRATEGIST — per-person ENGAGEMENT-STRATEGY brief for the human sales team
// (best channel/method, touch sequence, the human-executed LinkedIn play, the
// psychology, the first-impression opener, likely objections+rebuttals, and a
// no-fuckup do-not list). ADVISORY ONLY — no automation/scraping. BACK_STAGE.
export const strategist = {
  // POST /api/ops/strategist/brief { lead_id } -> generated brief.
  // status may be 'ready' | 'stub' | 'pending' (stub when no ANTHROPIC_API_KEY).
  generate: (leadId) =>
    api.post("/ops/strategist/brief", { lead_id: leadId }).then((r) => r.data),
  // GET /api/ops/strategist/brief?lead_id= -> latest brief for a lead (or null/404).
  forLead: (leadId) =>
    api.get("/ops/strategist/brief", { params: { lead_id: leadId } }).then((r) => r.data),
  // GET /api/ops/strategist/briefs/{id} -> a single brief by its own id.
  get: (id) => api.get(`/ops/strategist/briefs/${id}`).then((r) => r.data),
  // GET /api/ops/strategist/briefs -> recently generated briefs (array or { briefs:[] }).
  list: (limit = 50) =>
    api.get("/ops/strategist/briefs", { params: { limit } }).then((r) => r.data),
};

// Owner OAuth connections (LinkedIn / Microsoft / X / Meta / Reddit).
// Tokens live ENCRYPTED in Mongo server-side and are auto-refreshed by a
// background worker — this surface only ever sees status + account labels.
export const connections = {
  list: () => api.get("/ops/connections").then((r) => r.data),
  // Returns { authorize_url } — the page hands the browser straight to it.
  connect: (provider) => api.post(`/ops/connections/${provider}/connect`).then((r) => r.data),
  disconnect: (provider) =>
    api.post(`/ops/connections/${provider}/disconnect`).then((r) => r.data),
};

// AEGIS support console (ops/admin/support roles — the WALLED operator thread
// only ever comes back on the ops view; client_safe_only previews the client view).
export const support = {
  tickets: (params = {}) => api.get("/ops/support/tickets", { params }).then((r) => r.data),
  ticket: (id, clientSafeOnly = false) =>
    api
      .get(`/ops/support/tickets/${id}`, { params: { client_safe_only: clientSafeOnly } })
      .then((r) => r.data),
  reply: (id, body, new_status = null) =>
    api.post(`/ops/support/tickets/${id}/reply`, { body, new_status }).then((r) => r.data),
  escalate: (id, reason = "manual_ops_escalation") =>
    api.post(`/ops/support/tickets/${id}/escalate`, { reason }).then((r) => r.data),
  resolve: (id, opts = {}) =>
    api.post(`/ops/support/tickets/${id}/resolve`, opts).then((r) => r.data),
  sweep: () => api.post("/ops/support/sweep").then((r) => r.data),
};

export const admin = {
  leads: (limit = 100) => api.get(`/admin/leads?limit=${limit}`).then((r) => r.data),
  lead: (id) => api.get(`/admin/leads/${id}`).then((r) => r.data),
  trips: (params = {}) => api.get("/admin/trip-requests", { params }).then((r) => r.data),
  trip: (id) => api.get(`/admin/trip-requests/${id}`).then((r) => r.data),
  createTrip: (body) => api.post("/admin/trip-requests", body).then((r) => r.data),
  runQuote: (id) => api.post(`/admin/trip-requests/${id}/run-quote`).then((r) => r.data),
  quotes: (limit = 100) => api.get(`/admin/quotes?limit=${limit}`).then((r) => r.data),
  quote: (id) => api.get(`/admin/quotes/${id}`).then((r) => r.data),
  acceptQuote: (id, body) => api.post(`/admin/quotes/${id}/accept`, body).then((r) => r.data),
  followUp: (id) => api.post(`/admin/quotes/${id}/follow-up`).then((r) => r.data),
  bookings: (params = {}) => api.get("/admin/bookings", { params }).then((r) => r.data),
  bookingPnL: (id) => api.get(`/admin/bookings/${id}/pnl`).then((r) => r.data),
  clients: () => api.get("/admin/clients").then((r) => r.data),
};

// Public empty-leg marketplace (no auth — THE WALL enforced server-side).
export const marketplace = {
  list: (params = {}) => api.get("/public/empty-legs", { params }).then((r) => r.data),
  detail: (idOrSlug) => api.get(`/public/empty-legs/${idOrSlug}`).then((r) => r.data),
  // Quick reserve: capture -> quote FROM the held leg -> hold. 409 = already held.
  reserve: (idOrSlug, body) =>
    api.post(`/public/empty-legs/${idOrSlug}/reserve`, body).then((r) => r.data),
  // CONSENT-gated price-drop / route alert subscription (lead capture).
  alert: (body) => api.post("/public/empty-legs/alerts", body).then((r) => r.data),
};

export const widget = {
  brand: () => api.get("/public/brand").then((r) => r.data),
  quote: (body) => api.post("/public/quote", body).then((r) => r.data),
  byShareToken: (t) => api.get(`/public/quote/${t}`).then((r) => r.data),
  // Self-serve accept-and-book from the /q/:token page or the widget Reserve CTA.
  accept: (t, body) => api.post(`/public/quote/${t}/accept`, body).then((r) => r.data),
  // Airport typeahead for the widget From/To fields (public geo data, WALL-clean).
  airports: (q, limit = 8) =>
    api.get("/public/airports", { params: { q, limit } }).then((r) => r.data),
  concierge: (message, lead_id) =>
    api.post("/public/concierge", { message, lead_id }).then((r) => r.data),
};
